/**
 * Pure helpers for the admin Bulk Upload page. This module imports NOTHING and
 * touches no browser API - every decision that can be made from a file's name,
 * size and reported type lives here so it can be asserted directly.
 *
 * See the header of ../PhotoBulkUpload.tsx for the decisions these encode.
 */

// ---- constants -------------------------------------------------------------
export const MAX_FILES = 200
export const MAX_BYTES = 20 * 1024 * 1024 // 20 MB, non-GIF
export const MAX_GIF_BYTES = 8 * 1024 * 1024 // 8 MB, GIF (never re-encoded)
export const MAX_BATCH_OUTPUT_BYTES = 500 * 1024 * 1024
export const MAX_EDGE = 2560
export const JPEG_QUALITY = 0.85
export const MAX_MEGAPIXELS = 100
export const MAX_LABEL_LEN = 120
export const UPLOAD_TIMEOUT_MS = 120_000
export const INSERT_TIMEOUT_MS = 30_000
/** 1 week. Objects are immutable (ts+rand path, upsert:false), so this is always safe. */
export const CACHE_CONTROL = '604800'
export const CONSECUTIVE_FAILURE_ABORT = 5
/** PostgREST max_rows = 1000 and mobile usePhotos orders ASCENDING with no limit. */
export const ROW_WARN_THRESHOLD = 900

export const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const
export type AcceptedMime = (typeof ACCEPTED_MIME)[number]
export const ACCEPT_ATTR = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif'

export const REJECTED_EXTENSIONS = ['heic', 'heif', 'svg', 'svgz', 'pdf', 'tif', 'tiff', 'bmp', 'avif'] as const
export const SYSTEM_ARTIFACT_NAMES = ['thumbs.db', 'desktop.ini', '.ds_store', 'picasa.ini'] as const

export const STORAGE_PATH_RE = /^uploads\/gallery\/[0-9a-f-]{36}\/\d{8}\/\d+_[a-z0-9]+\.(jpg|png|webp|gif)$/

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The em dash used in every operator-facing string (U+2014). */
const DASH = '—'

// ---- classification --------------------------------------------------------
/** The minimal shape classifyFile needs; a real File satisfies it. */
export interface FileLike {
  name: string
  type: string
  size: number
}

export type Classification =
  | { ok: true; mime: AcceptedMime }
  | { ok: false; reason: string }
  /** System artifact: silently counted, no row. */
  | { ok: 'ignore' }

const HEIC_REASON = `HEIC/HEIF cannot be decoded in a browser ${DASH} export as JPEG first`

const baseName = (name: string): string => name.split('/').pop() ?? name

const extensionOf = (name: string): string => {
  const base = baseName(name)
  const dot = base.lastIndexOf('.')
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : ''
}

const mimeFromType = (type: string): AcceptedMime | null => {
  const t = type.split(';')[0]?.trim().toLowerCase() ?? ''
  if (t === 'image/jpg') return 'image/jpeg'
  return (ACCEPTED_MIME as readonly string[]).includes(t) ? (t as AcceptedMime) : null
}

const mimeFromExt = (ext: string): AcceptedMime | null => {
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
  if (ext === 'png') return 'image/png'
  if (ext === 'webp') return 'image/webp'
  if (ext === 'gif') return 'image/gif'
  return null
}

const rejectedExtensionReason = (ext: string): string | null => {
  if (ext === 'heic' || ext === 'heif') return HEIC_REASON
  if (ext === 'svg' || ext === 'svgz') return 'SVG is not accepted'
  if (ext === 'pdf') return 'PDF is not an image'
  if ((REJECTED_EXTENSIONS as readonly string[]).includes(ext)) return `${ext.toUpperCase()} files are not accepted`
  return null
}

/**
 * MIME first; when `type` is unusable fall back to the lowercased extension.
 * A rejected extension always loses, even with an image/* MIME. An unknown type
 * with an unknown extension is REJECTED, never guessed.
 */
export function classifyFile(file: FileLike): Classification {
  const base = baseName(file.name)
  const lower = base.toLowerCase()
  if (lower.startsWith('.') || (SYSTEM_ARTIFACT_NAMES as readonly string[]).includes(lower)) return { ok: 'ignore' }

  const ext = extensionOf(base)
  const rejected = rejectedExtensionReason(ext)
  if (rejected) return { ok: false, reason: rejected }

  const reported = file.type.toLowerCase()
  if (reported.includes('heic') || reported.includes('heif')) return { ok: false, reason: HEIC_REASON }
  if (reported === 'image/svg+xml') return { ok: false, reason: 'SVG is not accepted' }

  const mime = mimeFromType(file.type) ?? mimeFromExt(ext)
  if (!mime) return { ok: false, reason: 'unrecognized file type' }

  if (file.size <= 0) return { ok: false, reason: 'this file is empty' }
  if (mime === 'image/gif' && file.size > MAX_GIF_BYTES) {
    return { ok: false, reason: 'animated GIFs are capped at 8 MB' }
  }
  if (file.size > MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1)
    return { ok: false, reason: `too large (${mb} MB) ${DASH} the limit is 20 MB` }
  }
  return { ok: true, mime }
}

export function exceedsBatchBytes(totalBytes: number): boolean {
  return totalBytes > MAX_BATCH_OUTPUT_BYTES
}

/**
 * Splits a raw drop into rows to create, silently-ignored artifacts, and the
 * overflow past MAX_FILES. Truncation happens BEFORE any row state exists, so a
 * 3,000-file drop never becomes 3,000 DOM rows.
 */
export function partitionDroppedFiles<T extends FileLike>(
  files: T[],
  alreadyQueued: number,
): {
  accepted: T[]
  rejected: Array<{ file: T; reason: string }>
  ignoredCount: number
  overflowCount: number
} {
  const rejected: Array<{ file: T; reason: string }> = []
  let ignoredCount = 0
  const candidates: T[] = []

  for (const file of files) {
    const c = classifyFile(file)
    if (c.ok === 'ignore') ignoredCount += 1
    else if (c.ok === false) rejected.push({ file, reason: c.reason })
    else candidates.push(file)
  }

  candidates.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' }))

  const room = Math.max(0, MAX_FILES - alreadyQueued)
  const withinCap = candidates.slice(0, room)
  const overflowCount = candidates.length - withinCap.length

  const accepted: T[] = []
  let bytes = 0
  for (const file of withinCap) {
    if (exceedsBatchBytes(bytes + file.size)) {
      rejected.push({ file, reason: `this batch exceeds 500 MB of photos ${DASH} remove some files` })
      continue
    }
    bytes += file.size
    accepted.push(file)
  }

  return { accepted, rejected, ignoredCount, overflowCount }
}

// ---- labels ----------------------------------------------------------------
/** C0 + DEL + zero-width + bidi controls. Built from escapes so the source stays ASCII. */
const UNSAFE_CHARS = new RegExp(
  '[\\u0000-\\u001F\\u007F\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069]',
  'g',
)

/**
 * Strip the last extension, [_-]+ -> space, collapse whitespace, strip C0/DEL
 * and bidi controls, trim, truncate to MAX_LABEL_LEN on a word boundary.
 * Returns 'Untitled photo' when the result is empty.
 */
/**
 * Every truncation in this file slices UTF-16 CODE UNITS, so a cut can land
 * between the halves of a surrogate pair. The word-boundary retreat usually
 * rescues a label, but an emoji-dense one with no space past the limit cuts
 * straight through - and a lone surrogate in a JSON body is rejected by Postgres
 * ("unsupported Unicode escape sequence"), which is a baffling failure for a
 * label the operator typed. Dropping the orphaned half costs one character.
 */
const dropLoneSurrogate = (s: string): string => (/[\uD800-\uDBFF]$/.test(s) ? s.slice(0, -1) : s)

export function sanitizeLabel(filename: string): string {
  const base = baseName(filename)
  const dot = base.lastIndexOf('.')
  const stem = dot > 0 ? base.slice(0, dot) : base.startsWith('.') ? '' : base

  let out = stem.replace(UNSAFE_CHARS, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()

  if (out.length > MAX_LABEL_LEN) {
    const cut = dropLoneSurrogate(out.slice(0, MAX_LABEL_LEN))
    const space = cut.lastIndexOf(' ')
    out = (space > 0 ? cut.slice(0, space) : cut).trim()
  }
  return out === '' ? 'Untitled photo' : out
}

/**
 * The same label contract as sanitizeLabel, for a label the OPERATOR TYPED.
 *
 * sanitizeLabel only ever runs on a filename, so until this existed the one
 * label path a human actually controls was the one path with no validation: the
 * row input writes e.target.value straight through and the queue inserts it
 * unfiltered. photos.label is NOT NULL but is neither length- nor
 * content-checked, and the value lands on a public alumni-app surface.
 *
 * Deliberately NOT extension-stripping: that step is filename-specific and
 * would eat the tail of a legitimate typed label ("Class of 1998. Reunion").
 */
export function clampLabel(raw: string): string {
  let out = raw.replace(UNSAFE_CHARS, '').replace(/\s+/g, ' ').trim()

  if (out.length > MAX_LABEL_LEN) {
    const cut = dropLoneSurrogate(out.slice(0, MAX_LABEL_LEN))
    const space = cut.lastIndexOf(' ')
    out = (space > 0 ? cut.slice(0, space) : cut).trim()
  }
  return out === '' ? 'Untitled photo' : out
}

// ---- paths and URLs --------------------------------------------------------
export function extForMime(mime: string): 'jpg' | 'png' | 'webp' | 'gif' {
  if (mime === 'image/jpeg') return 'jpg'
  if (mime === 'image/png') return 'png'
  if (mime === 'image/webp') return 'webp'
  if (mime === 'image/gif') return 'gif'
  throw new Error(`unsupported content type: ${mime}`)
}

/**
 * `uploads/gallery/<albumId>/<YYYYMMDD>/<epochMs>_<rand>.<ext>`.
 * The FILENAME NEVER CONTRIBUTES: getPublicUrl() uses encodeURI, which leaves
 * `?`, `#` and `/` unescaped, so a hostile name would silently truncate the URL.
 */
export function buildStoragePath(albumId: string, at: Date, mime: string): string {
  if (!UUID_RE.test(albumId)) throw new Error(`invalid album id: ${albumId}`)
  const ext = extForMime(mime)
  const y = at.getUTCFullYear()
  const m = String(at.getUTCMonth() + 1).padStart(2, '0')
  const d = String(at.getUTCDate()).padStart(2, '0')
  const rand = Math.random().toString(36).slice(2, 10).padEnd(8, '0')
  return `uploads/gallery/${albumId}/${y}${m}${d}/${at.getTime()}_${rand}.${ext}`
}

export function validatePublicUrl(
  publicUrl: string,
  expectedOrigin: string,
  ext: string,
): { ok: true } | { ok: false; reason: string } {
  let url: URL
  let expected: URL
  try {
    url = new URL(publicUrl)
    expected = new URL(expectedOrigin)
  } catch {
    return { ok: false, reason: 'the storage URL could not be parsed' }
  }
  if (url.origin !== expected.origin) return { ok: false, reason: 'the storage URL points at an unexpected host' }
  if (url.search !== '' || url.hash !== '') {
    return { ok: false, reason: 'the storage URL contains unescaped characters' }
  }
  if (!url.pathname.toLowerCase().endsWith(`.${ext}`)) {
    return { ok: false, reason: `the storage URL does not end in .${ext}` }
  }
  return { ok: true }
}

// ---- ordering --------------------------------------------------------------
/** ISO-8601 UTC. Strictly increasing and distinct across indices. */
export function createdAtForIndex(batchStartMs: number, index: number): string {
  return new Date(batchStartMs + index).toISOString()
}

/** max(now, latestExisting + 1000) - never interleave into the album's history. */
export function clampBatchStart(nowMs: number, latestExistingMs: number | null): number {
  if (latestExistingMs == null) return nowMs
  return Math.max(nowMs, latestExistingMs + 1000)
}

export function thresholdWarning(postBatchTotal: number): string | null {
  if (postBatchTotal < ROW_WARN_THRESHOLD) return null
  return `This batch brings the gallery to ${postBatchTotal} photos; the app currently loads at most 1000.`
}

// ---- bytes -----------------------------------------------------------------
const startsWith = (head: Uint8Array, bytes: number[], offset = 0): boolean =>
  head.length >= offset + bytes.length && bytes.every((b, i) => head[offset + i] === b)

const A = (s: string): number[] => Array.from(s).map((c) => c.charCodeAt(0))

/** First-12-bytes magic-number sniff. Returns null when nothing matches. */
export function sniffImageType(head: Uint8Array): AcceptedMime | null {
  if (startsWith(head, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'
  if (startsWith(head, A('GIF87a')) || startsWith(head, A('GIF89a'))) return 'image/gif'
  if (startsWith(head, A('RIFF')) && startsWith(head, A('WEBP'), 8)) return 'image/webp'
  return null
}

/** Prefer the re-encode only when it actually helps; a resize always wins. */
export function chooseUploadBody(
  originalSize: number,
  encodedSize: number,
  didResize: boolean,
): 'encoded' | 'original' {
  if (didResize) return 'encoded'
  return encodedSize < originalSize ? 'encoded' : 'original'
}

// ---- errors ----------------------------------------------------------------
const readString = (o: Record<string, unknown>, key: string): string | null => {
  const v = o[key]
  return typeof v === 'string' && v.length > 0 ? v : null
}

/**
 * supabase-js errors are PLAIN OBJECTS, not Error instances. Storage carries the
 * meaningful name in `error`/`statusCode`; PostgREST carries `code` + `message`.
 * NEVER write `e instanceof Error` against one of these.
 */
export function describeError(e: unknown): string {
  const truncate = (s: string) => (s.length > 200 ? dropLoneSurrogate(s.slice(0, 200)) : s)
  if (typeof e === 'string') return truncate(e)
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>
    const code = readString(o, 'code')
    const status = o.statusCode ?? o.status
    const base =
      readString(o, 'message') ??
      readString(o, 'error') ??
      (status != null ? `status ${String(status)}` : null) ??
      (() => {
        try {
          return JSON.stringify(e)
        } catch {
          return 'unknown error'
        }
      })()
    return truncate(code ? `${code}: ${base}` : base)
  }
  return truncate(e == null ? 'unknown error' : String(e))
}

export type FailureClass = 'retryable' | 'fatal' | 'auth' | 'permission' | 'album-gone' | 'network'

export function classifyFailure(e: unknown): FailureClass {
  const o = e && typeof e === 'object' ? (e as Record<string, unknown>) : {}
  const code = typeof o.code === 'string' ? o.code : ''
  const status = Number(o.status ?? o.statusCode ?? 0)
  const text = describeError(e).toLowerCase()

  if (code === '42501' || text.includes('row-level security')) return 'permission'
  if (code === '23503' || text.includes('foreign key')) return 'album-gone'
  if (code === '23505') return 'fatal'
  if (status === 401 || text.includes('jwt expired') || text.includes('invalid refresh token')) return 'auth'
  if (
    code === '57014' ||
    code === '55P03' ||
    text.includes('audit') ||
    text.includes('lock') ||
    text.includes('statement timeout')
  ) {
    return 'retryable'
  }
  if (
    e instanceof TypeError ||
    text.includes('failed to fetch') ||
    text.includes('network') ||
    text.includes('load failed')
  ) {
    return 'network'
  }
  return 'retryable'
}

/** Operator-facing reason for a failure raised while READING or DECODING a file. */
export function processingFailureReason(e: unknown): string {
  const name = e && typeof e === 'object' ? String((e as Record<string, unknown>).name ?? '') : ''
  const msg = describeError(e)
  if (name === 'NotReadableError' || msg.toLowerCase().includes('could not be read')) {
    return `could not be read ${DASH} it may have moved, or it is not downloaded from cloud storage`
  }
  return msg
}
